import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { useMessaging } from '../contexts/MessagingContext';
import { useNotifications } from '../contexts/NotificationContext';

/* Single source of truth for the Lending Network page set. The topbar
   strip and this bottom bar are a paired set — both render only on
   these paths, both link to the same 5 destinations. */
export const LENDING_NETWORK_PATHS = [
    '/app/leadflow/feed',
    '/app/my-network',
    '/app/messaging',
    '/app/my-agent-profile',
    '/app/edit-profile',
    '/app/create-profile',
    '/app/business/pages',
    '/app/my-company',
];

export const isLendingNetworkPath = (pathname) => {
    // ViewAgentProfile: /app/profile/<id> (someone else's profile — social).
    // Excludes /app/profile (own read-only) and /app/profile/complete (wizard),
    // which are account pages, not social.
    const profileMatch = pathname.match(/^\/app\/profile\/([^/]+)/);
    if (profileMatch && profileMatch[1] !== 'complete') return true;

    return LENDING_NETWORK_PATHS.some((p) => pathname.startsWith(p));
};

const NAV_ITEMS = [
    { label: 'For Business', icon: 'pi pi-building', path: '/app/business/pages', matchIncludes: true },
    { label: 'Feed', icon: 'pi pi-comments', path: '/app/leadflow/feed', matchIncludes: true },
    { label: 'Connections', icon: 'pi pi-users', path: '/app/my-network', matchIncludes: false },
    { label: 'Messages', icon: 'pi pi-envelope', path: '/app/messaging', matchIncludes: false },
    { label: 'Me', icon: 'pi pi-user', path: '/app/my-agent-profile', matchIncludes: false },
];

const MobileBottomNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { unreadCount: unreadMsgCount } = useMessaging();
    const { pendingConnectionsCount, newFeedPostsCount, markConnectionsViewed, markFeedViewed } = useNotifications();

    /* Render only on Lending Network pages — paired with the topbar strip. */
    if (!isLendingNetworkPath(location.pathname)) {
        return null;
    }

    const getBadgeCount = (path) => {
        if (path === '/app/leadflow/feed') return newFeedPostsCount || 0;
        if (path === '/app/my-network') return pendingConnectionsCount || 0;
        if (path === '/app/messaging') return unreadMsgCount || 0;
        return 0;
    };

    const isActive = (item) => {
        if (item.matchIncludes) return location.pathname.startsWith(item.path);
        return location.pathname === item.path;
    };

    const handleClick = (item) => {
        if (item.path === '/app/my-network') markConnectionsViewed();
        if (item.path === '/app/leadflow/feed') markFeedViewed();
        navigate(item.path);
    };

    return (
        /* Real <button>s, not clickable <div>s: a button is focusable and fires
           onClick for both Enter and Space natively, so this bar is reachable by
           keyboard without hand-rolled key handlers. */
        <nav className="mobile-bottom-nav" aria-label="Lending Network">
            {NAV_ITEMS.map((item) => {
                const badgeCount = getBadgeCount(item.path);
                const active = isActive(item);

                return (
                    <button
                        key={item.path}
                        type="button"
                        className={`mobile-bottom-nav-item${active ? ' active' : ''}`}
                        onClick={() => handleClick(item)}
                        aria-current={active ? 'page' : undefined}
                    >
                        <span className="mobile-bottom-nav-icon" style={{ position: 'relative', display: 'inline-block' }}>
                            <i className={item.icon}></i>
                            {badgeCount > 0 && (
                                <Badge
                                    value={badgeCount > 99 ? '99+' : badgeCount}
                                    severity="danger"
                                />
                            )}
                        </span>
                        <span className="mobile-bottom-nav-label">{item.label}</span>
                    </button>
                );
            })}
        </nav>
    );
};

export default MobileBottomNav;
